/**
 * useRequirementsGeneration Hook
 * 
 * Custom hook for AI requirements generation with proper error handling
 * Similar to useAIGeneration but focused only on requirements refinement
 */

import { useState, useCallback } from 'react';
import jobDescriptionService from '../../../../../Service/job.description.service';
import { openNotification } from '../../../../../Components/Notification';
import { Requirements } from '../../../../../Entities/JobDescription';
import { RefinementContext } from '../utils/contextBuilder';
import { smartMergeRequirements, MergeResult } from '../utils/smartMerge';
import { message } from 'antd';

interface UseRequirementsGenerationReturn {
  generateRequirements: (
    prompt: string,
    context?: RefinementContext,
    currentRequirements?: Requirements[]
  ) => Promise<{ requirements: Requirements[]; mergeResult: MergeResult } | null>;
  isGenerating: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * useRequirementsGeneration Hook
 * 
 * Features:
 * - AI requirements generation with loading states
 * - Comprehensive error handling
 * - Input validation
 * - Type-safe return values
 */
export const useRequirementsGeneration = (): UseRequirementsGenerationReturn => {
  // State management
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Clear error state
   * Utility function for error state management
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Handle input validation errors
   * Helper function to reduce cognitive complexity
   */
  const handleValidationError = useCallback((errorMsg: string) => {
    setError(errorMsg);
    //openNotification('error', 'Invalid Input', errorMsg);
    message.error('Invalid input: ' + errorMsg);
  }, []);

  /**
   * Handle API errors
   * Helper function to reduce cognitive complexity
   */
  const handleAPIError = useCallback((err: unknown) => {
    let errorMsg: string;
    
    if (err instanceof Error) {
      errorMsg = err.message;
    } else if (typeof err === 'string') {
      errorMsg = err;
    } else {
      errorMsg = 'An unexpected error occurred while generating requirements. Please try again.';
    }
    
    setError(errorMsg);
    //openNotification('error', 'Generation Failed', errorMsg);
    message.error('Generation failed: ' + errorMsg);
  }, []);

  /**
   * Format AI response to Requirements array
   * Transforms API response to match frontend Requirements structure
   */
  const formatRequirementsResponse = useCallback((response: any): Requirements[] => {
    if (!response || !response.requirements || !Array.isArray(response.requirements)) {
      throw new Error('Invalid response structure from AI service');
    }

    return response.requirements.map((req: any, index: number) => ({
      id: req.id || index + 1,
      topic: req.topic || '', // Always empty string per FR-009
      description: req.description || '',
      mustMatch: req.mustMatch ?? true,
    }));
  }, []);

  /**
   * Generate requirements from AI prompt with context-aware refinement
   * Main function for AI requirements generation with comprehensive error handling
   * 
   * @param prompt - User's refinement prompt
   * @param context - Optional refinement context (Step 0, Step 1, history)
   * @param currentRequirements - Current requirements for smart merge
   * @returns Merged requirements and merge result, or null on error
   */
  const generateRequirements = useCallback(async (
    prompt: string,
    context?: RefinementContext,
    currentRequirements?: Requirements[]
  ): Promise<{ requirements: Requirements[]; mergeResult: MergeResult } | null> => {
    // Input validation
    if (!prompt?.trim()) {
      handleValidationError('Prompt cannot be empty. Please provide a description for refining requirements.');
      return null;
    }

    // Check prompt length
    if (prompt.trim().length < 10) {
      handleValidationError('Prompt is too short. Please provide a more detailed description (at least 10 characters).');
      return null;
    }

    try {
      // Set loading state
      setIsGenerating(true);
      setError(null);
      
      // Call AI service with context
      const response = await jobDescriptionService.generateRequirements(prompt.trim(), context);
      
      // Validate response
      if (!response) {
        throw new Error('No response received from AI service');
      }
      
      // Format response to Requirements array
      const aiRequirements = formatRequirementsResponse(response);
      
      // Smart merge with current requirements if provided
      const mergeResult = smartMergeRequirements(
        aiRequirements,
        currentRequirements || []
      );
      
      // Show success notification
      const changeCount = mergeResult.changes.added.length + 
                          mergeResult.changes.modified.length + 
                          mergeResult.changes.removed.length;
      
      if (changeCount > 0) {
        openNotification(
          'success',
          'Requirements Refined',
          `AI has successfully refined requirements. ${mergeResult.changes.added.length} added, ${mergeResult.changes.modified.length} modified, ${mergeResult.changes.removed.length} removed.`
        );
        
      } else {
        openNotification(
          'success',
          'Requirements Generated',
          'AI has successfully generated requirements. They have been updated in the form.'
        );
      }
      
      return {
        requirements: mergeResult.mergedRequirements,
        mergeResult,
      };
      
    } catch (err) {
      // Handle API errors
      handleAPIError(err);
      return null;
      
    } finally {
      // Always clear loading state
      setIsGenerating(false);
    }
  }, [handleValidationError, handleAPIError, formatRequirementsResponse]);

  // Return hook interface
  return {
    generateRequirements,
    isGenerating,
    error,
    clearError,
  };
};

